import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Modal, Spinner } from "react-bootstrap";
import {
  Search,
  Plus,
  Pencil,
  Copy,
  Power,
  ArrowLeft,
  Save,
  AlertCircle,
  CheckCircle2,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Eye,
  Settings,
  PlayCircle,
  ShieldCheck,
  UploadCloud,
  BarChart3,
  Trophy,
  GraduationCap,
  FileText,
  Braces,
} from "lucide-react";
import TestCategoryService, {
  type TestCategory,
} from "../../_services/TestCategoryService";
import LayoutParamsEditor from "./LayoutParamsEditor";
import {
  TestTakingTab,
  ProctoringTab,
  SubmissionTab,
  ResultsTab,
} from "./ConfigEditor";
import Toggle from "./Toggle";
import MarksMessageEditor from "./MarksMessageEditor";
import "./test-category-settings.css";

// ---------------------------------------------------------------------------
// Small shared bits
// ---------------------------------------------------------------------------

const SwitchRow: React.FC<{
  label: string;
  checked: boolean;
  onChange: () => void;
}> = ({ label, checked, onChange }) => (
  <div className="tcs-switch-row">
    <span className="tcs-switch-label">{label}</span>
    <Toggle checked={checked} onChange={onChange} aria-label={label} />
  </div>
);

const Field: React.FC<{
  label: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <div className="tcs-field">
    <label className="tcs-label">{label}</label>
    {hint && <p className="tcs-hint">{hint}</p>}
    {children}
  </div>
);

/** Raw JSON editor with local text state + parse guard. */
const RawJsonEditor: React.FC<{
  label: string;
  hint?: string;
  value: any;
  onChange: (parsed: any) => void;
  rows?: number;
}> = ({ label, hint, value, onChange, rows = 18 }) => {
  const [text, setText] = useState(() => JSON.stringify(value ?? {}, null, 2));
  const [error, setError] = useState("");

  // Re-sync when the category being edited changes identity.
  useEffect(() => {
    setText(JSON.stringify(value ?? {}, null, 2));
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handle = (next: string) => {
    setText(next);
    try {
      const parsed = JSON.parse(next);
      setError("");
      onChange(parsed);
    } catch {
      setError("Invalid JSON — fix before saving");
    }
  };

  return (
    <Field label={label} hint={hint}>
      {error && (
        <div className="tcs-alert tcs-alert-danger">
          <AlertCircle size={14} style={{ marginRight: 6 }} />
          {error}
        </div>
      )}
      <textarea
        className="tcs-textarea tcs-code"
        rows={rows}
        value={text}
        onChange={(e) => handle(e.target.value)}
      />
    </Field>
  );
};

// ---------------------------------------------------------------------------
// List View
// ---------------------------------------------------------------------------

interface ListViewProps {
  categories: TestCategory[];
  onOpen: (cat: TestCategory, mode: "view" | "edit") => void;
  onClone: (cat: TestCategory) => void;
  onToggle: (cat: TestCategory) => void;
  onAdd: () => void;
}

const ListView: React.FC<ListViewProps> = ({
  categories,
  onOpen,
  onClone,
  onToggle,
  onAdd,
}) => {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<"id" | "name" | "sortOrder">(
    "sortOrder",
  );
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const toggleSort = (key: "id" | "name" | "sortOrder") => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? categories.filter(
          (c) =>
            (c.name || "").toLowerCase().includes(q) ||
            String(c.id).includes(q),
        )
      : categories;

    const dir = sortDir === "asc" ? 1 : -1;
    return [...base].sort((a, b) => {
      let cmp: number;
      if (sortKey === "name") {
        cmp = (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        });
      } else {
        // id / sortOrder — numeric; nulls sort last regardless of direction
        const av = a[sortKey];
        const bv = b[sortKey];
        if (av == null && bv == null) cmp = 0;
        else if (av == null) return 1;
        else if (bv == null) return -1;
        else cmp = Number(av) - Number(bv);
      }
      return cmp * dir;
    });
  }, [categories, search, sortKey, sortDir]);

  const hasLayout = (c: TestCategory) =>
    c.testLayoutParameters &&
    Object.keys(c.testLayoutParameters || {}).length > 0;

  const SortIcon: React.FC<{ col: "id" | "name" | "sortOrder" }> = ({
    col,
  }) => {
    if (sortKey !== col)
      return (
        <ChevronsUpDown
          size={13}
          style={{ opacity: 0.4, verticalAlign: "middle", marginLeft: 4 }}
        />
      );
    return sortDir === "asc" ? (
      <ChevronUp size={13} style={{ verticalAlign: "middle", marginLeft: 4 }} />
    ) : (
      <ChevronDown
        size={13}
        style={{ verticalAlign: "middle", marginLeft: 4 }}
      />
    );
  };

  return (
    <div className="tcs-card">
      <div className="tcs-toolbar">
        <div className="tcs-search">
          <Search size={15} />
          <input
            type="text"
            placeholder="Search by ID or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button className="tcs-btn tcs-btn-primary" onClick={onAdd}>
          <Plus size={15} /> Add Category
        </button>
      </div>

      <div className="tcs-table-wrap">
        <table className="tcs-table">
          <thead>
            <tr>
              <th
                className="tcs-center tcs-th-sort"
                style={{ width: 70 }}
                onClick={() => toggleSort("id")}
              >
                ID
                <SortIcon col="id" />
              </th>
              <th className="tcs-th-sort" onClick={() => toggleSort("name")}>
                Category
                <SortIcon col="name" />
              </th>
              <th
                className="tcs-center tcs-th-sort"
                onClick={() => toggleSort("sortOrder")}
              >
                Order
                <SortIcon col="sortOrder" />
              </th>
              <th className="tcs-center">Portal</th>
              <th className="tcs-center">Instructions</th>
              <th className="tcs-center">Status</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="tcs-empty">
                  {search ? "No matching categories" : "No categories found"}
                </td>
              </tr>
            ) : (
              filtered.map((cat) => (
                <tr
                  key={cat.id}
                  className={`tcs-row-click ${
                    cat.enableStatus === 0 ? "tcs-row-disabled" : ""
                  }`}
                  onClick={() => onOpen(cat, "view")}
                  title="Click to view settings"
                >
                  <td className="tcs-center">
                    <span className="tcs-pill tcs-pill-neutral">{cat.id}</span>
                  </td>
                  <td className="tcs-cat-name">{cat.name}</td>
                  <td className="tcs-center">{cat.sortOrder ?? "—"}</td>
                  <td className="tcs-center">
                    <span
                      className={`tcs-pill ${
                        cat.displayInPortal
                          ? "tcs-pill-success"
                          : "tcs-pill-neutral"
                      }`}
                    >
                      {cat.displayInPortal ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="tcs-center">
                    <span
                      className={`tcs-pill ${
                        hasLayout(cat) ? "tcs-pill-success" : "tcs-pill-neutral"
                      }`}
                    >
                      {hasLayout(cat) ? "Configured" : "—"}
                    </span>
                  </td>
                  <td className="tcs-center">
                    <span
                      className={`tcs-pill ${
                        cat.enableStatus === 0
                          ? "tcs-pill-danger"
                          : "tcs-pill-success"
                      }`}
                    >
                      {cat.enableStatus === 0 ? "Disabled" : "Enabled"}
                    </span>
                  </td>
                  <td>
                    <div className="tcs-actions">
                      <button
                        className="tcs-icon-btn"
                        title="View"
                        aria-label={`View ${cat.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpen(cat, "view");
                        }}
                      >
                        <Eye size={15} />
                      </button>
                      <button
                        className="tcs-icon-btn"
                        title="Edit"
                        aria-label={`Edit ${cat.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpen(cat, "edit");
                        }}
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        className="tcs-icon-btn"
                        title="Clone"
                        aria-label={`Clone ${cat.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onClone(cat);
                        }}
                      >
                        <Copy size={15} />
                      </button>
                      <button
                        className={`tcs-icon-btn ${
                          cat.enableStatus === 0
                            ? "tcs-icon-btn-success"
                            : "tcs-icon-btn-danger"
                        }`}
                        title={cat.enableStatus === 0 ? "Enable" : "Disable"}
                        aria-label={`${
                          cat.enableStatus === 0 ? "Enable" : "Disable"
                        } ${cat.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggle(cat);
                        }}
                      >
                        <Power size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Detail View
// ---------------------------------------------------------------------------

// Grouped by admin intent — a tab may mix DB columns and Config JSON keys.
const TABS = [
  { key: "general", label: "General", icon: Settings },
  { key: "taking", label: "Test Taking", icon: PlayCircle },
  { key: "proctoring", label: "Proctoring & Security", icon: ShieldCheck },
  { key: "submission", label: "Submission & Uploads", icon: UploadCloud },
  { key: "results", label: "Results & Analysis", icon: BarChart3 },
  { key: "ranking", label: "Ranking", icon: Trophy },
  { key: "grading", label: "Grading & Attendance", icon: GraduationCap },
  { key: "instructions", label: "Instructions & Layout", icon: FileText },
  { key: "raw", label: "Raw JSON", icon: Braces },
];

interface DetailViewProps {
  category: TestCategory;
  initialMode: "view" | "edit";
  onBack: () => void;
  onSaved: () => void;
}

const DetailView: React.FC<DetailViewProps> = ({
  category,
  initialMode,
  onBack,
  onSaved,
}) => {
  const [mode, setMode] = useState<"view" | "edit">(initialMode);
  const readOnly = mode === "view";
  const [tab, setTab] = useState("general");
  const [form, setForm] = useState<TestCategory>({ ...category });
  const [config, setConfig] = useState<any>(category.config || {});
  const [layout, setLayout] = useState<any>(
    category.testLayoutParameters || {},
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  // Snapshot of the last saved state — drives the "Unsaved changes" chip and
  // the discard confirmation on Back/Cancel.
  const savedSnapshot = useRef(
    JSON.stringify({
      form: { ...category },
      config: category.config || {},
      layout: category.testLayoutParameters || {},
    }),
  );
  const dirty =
    JSON.stringify({ form, config, layout }) !== savedSnapshot.current;

  const setField = (key: keyof TestCategory, value: any) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleField = (key: keyof TestCategory) =>
    setForm((prev) => ({ ...prev, [key]: prev[key] ? 0 : 1 }));

  const requestBack = () => {
    if (dirty) setConfirmLeave(true);
    else onBack();
  };

  const handleSave = async () => {
    setError("");
    setSuccess(false);

    if (!form.name?.trim()) {
      setError("Category name is required");
      setTab("general");
      return;
    }

    setSaving(true);
    try {
      await TestCategoryService.updateCategory(form.id, {
        ...form,
        config,
        testLayoutParameters: layout,
      });
      savedSnapshot.current = JSON.stringify({ form, config, layout });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="tcs-detail-bar">
        <div className="tcs-detail-title">
          <button className="tcs-btn tcs-btn-secondary" onClick={requestBack}>
            <ArrowLeft size={15} /> Back
          </button>
          <h2>{form.name}</h2>
        </div>
        <div className="tcs-detail-actions">
          {readOnly ? (
            <>
              <span className="tcs-mode-pill">
                <Eye size={13} /> View mode
              </span>
              <button
                className="tcs-btn tcs-btn-primary"
                onClick={() => setMode("edit")}
              >
                <Pencil size={14} /> Edit Settings
              </button>
            </>
          ) : (
            <>
              {dirty && !saving && (
                <span className="tcs-dirty-note">Unsaved changes</span>
              )}
              {success && !dirty && (
                <span className="tcs-saved-note">
                  <CheckCircle2 size={14} style={{ marginRight: 4 }} />
                  Saved
                </span>
              )}
              <button
                className="tcs-btn tcs-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner role="status" size="sm" animation="border" />{" "}
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={15} /> Save Changes
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="tcs-alert tcs-alert-danger">
          <AlertCircle size={14} style={{ marginRight: 6 }} />
          {error}
        </div>
      )}

      <div className="tcs-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`tcs-tab ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      <div className="tcs-card" style={{ padding: 18 }}>
        {/* fieldset[disabled] natively disables every input/select/textarea/
            button inside — read-only view without threading a prop through
            each control. Rich-text editors are covered by .tcs-readonly CSS. */}
        <fieldset
          className={`tcs-fieldset ${readOnly ? "tcs-readonly" : ""}`}
          disabled={readOnly}
        >
        {/* ---------------- General ---------------- */}
        {tab === "general" && (
          <div className="tcs-grid-2">
            <Field label="Category Name">
              <input
                className="tcs-input"
                value={form.name || ""}
                onChange={(e) => setField("name", e.target.value)}
              />
            </Field>
            <Field label="Sort Order">
              <input
                type="number"
                className="tcs-input"
                value={form.sortOrder ?? 0}
                onChange={(e) => setField("sortOrder", Number(e.target.value))}
              />
            </Field>
            <Field label="Weightage">
              <input
                type="number"
                step="0.01"
                className="tcs-input"
                value={form.weigtage ?? 0}
                onChange={(e) =>
                  setField("weigtage", parseFloat(e.target.value) || 0)
                }
              />
            </Field>
            <Field label="Test Weightage">
              <input
                type="number"
                step="0.01"
                className="tcs-input"
                value={form.testWeigtage ?? 0}
                onChange={(e) =>
                  setField("testWeigtage", parseFloat(e.target.value) || 0)
                }
              />
            </Field>
            <div style={{ gridColumn: "1 / -1" }}>
              <SwitchRow
                label="Calculate Total"
                checked={!!form.calculateTotal}
                onChange={() => toggleField("calculateTotal")}
              />
              <SwitchRow
                label="Display In Portal"
                checked={!!form.displayInPortal}
                onChange={() => toggleField("displayInPortal")}
              />
            </div>
          </div>
        )}

        {/* ---------------- Test Taking ---------------- */}
        {tab === "taking" && (
          <TestTakingTab
            form={form}
            setField={setField}
            toggleField={toggleField}
            config={config}
            setConfig={setConfig}
          />
        )}

        {/* ---------------- Proctoring & Security ---------------- */}
        {tab === "proctoring" && (
          <ProctoringTab
            form={form}
            setField={setField}
            toggleField={toggleField}
            config={config}
            setConfig={setConfig}
          />
        )}

        {/* ---------------- Submission & Uploads ---------------- */}
        {tab === "submission" && (
          <SubmissionTab
            form={form}
            setField={setField}
            toggleField={toggleField}
            config={config}
            setConfig={setConfig}
          />
        )}

        {/* ---------------- Results & Analysis ---------------- */}
        {tab === "results" && (
          <ResultsTab
            form={form}
            setField={setField}
            toggleField={toggleField}
            config={config}
            setConfig={setConfig}
          />
        )}

        {/* ---------------- Ranking ---------------- */}
        {tab === "ranking" && (
          <div>
            <SwitchRow
              label="Rank In Dossier"
              checked={!!form.rankInDossier}
              onChange={() => toggleField("rankInDossier")}
            />
            <SwitchRow
              label="Do Not Compute Rank"
              checked={!!form.doNotComputeRank}
              onChange={() => toggleField("doNotComputeRank")}
            />
            <SwitchRow
              label="Invalidate Lowest Section"
              checked={!!form.invalidateLowestSection}
              onChange={() => toggleField("invalidateLowestSection")}
            />
            {!!form.invalidateLowestSection && (
              <div style={{ marginTop: 14 }}>
                <Field label="Number Of Sections To Invalidate">
                  <input
                    className="tcs-input tcs-input-sm"
                    value={form.numberOfSectionsToInvalidate || ""}
                    onChange={(e) =>
                      setField("numberOfSectionsToInvalidate", e.target.value)
                    }
                  />
                </Field>
              </div>
            )}
            <div className="tcs-grid-2" style={{ marginTop: 14 }}>
              <Field label="Batch Rank Label">
                <input
                  className="tcs-input"
                  value={form.batchRank || ""}
                  onChange={(e) => setField("batchRank", e.target.value)}
                />
              </Field>
              <Field label="School Rank Label">
                <input
                  className="tcs-input"
                  value={form.schoolRank || ""}
                  onChange={(e) => setField("schoolRank", e.target.value)}
                />
              </Field>
              <Field label="Test Rank Label">
                <input
                  className="tcs-input"
                  value={form.testRank || ""}
                  onChange={(e) => setField("testRank", e.target.value)}
                />
              </Field>
              <Field label="Manual Rank Label">
                <input
                  className="tcs-input"
                  value={form.manualRank || ""}
                  onChange={(e) => setField("manualRank", e.target.value)}
                />
              </Field>
            </div>
          </div>
        )}

        {/* ---------------- Grading & Attendance ---------------- */}
        {tab === "grading" && (
          <div>
            <SwitchRow
              label="Grade Entry"
              checked={!!form.gradeEntry}
              onChange={() => toggleField("gradeEntry")}
            />
            <SwitchRow
              label="Attendance Entry"
              checked={!!form.attendanceEntry}
              onChange={() => toggleField("attendanceEntry")}
            />
            <div style={{ marginTop: 14 }}>
              <Field label="Grade Validation">
                <input
                  className="tcs-input"
                  value={form.gradeValidation || ""}
                  onChange={(e) => setField("gradeValidation", e.target.value)}
                />
              </Field>
            </div>
            <div style={{ marginTop: 6 }}>
              <label className="tcs-label">Marks Message</label>
              <MarksMessageEditor
                value={form.marksMessage}
                onChange={(raw) => setField("marksMessage", raw)}
              />
            </div>
          </div>
        )}

        {/* ---------------- Instructions & Layout ---------------- */}
        {tab === "instructions" && (
          <LayoutParamsEditor value={layout} onChange={setLayout} />
        )}

        {/* ---------------- Raw JSON ---------------- */}
        {tab === "raw" && (
          <div>
            <RawJsonEditor
              label="Config (raw JSON)"
              hint="Full Config column. Overrides values set in the Test Config tab."
              value={config}
              onChange={setConfig}
            />
            <RawJsonEditor
              label="Test Layout Parameters (raw JSON)"
              hint="Full TestLayoutParameters column, including any keys not exposed in the Instructions tab."
              value={layout}
              onChange={setLayout}
            />
          </div>
        )}
        </fieldset>
      </div>

      {!readOnly && (
        <div className="tcs-footer-actions">
          <button className="tcs-btn tcs-btn-secondary" onClick={requestBack}>
            Cancel
          </button>
          <button
            className="tcs-btn tcs-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}

      {/* Discard-changes confirmation */}
      <Modal show={confirmLeave} onHide={() => setConfirmLeave(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            Discard Changes
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have unsaved changes to <strong>{form.name}</strong>. Leave
          without saving?
        </Modal.Body>
        <Modal.Footer>
          <button
            className="tcs-btn tcs-btn-secondary"
            onClick={() => setConfirmLeave(false)}
          >
            Keep Editing
          </button>
          <button className="tcs-btn tcs-btn-danger" onClick={onBack}>
            Discard Changes
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

const TestCategorySettingsPage: React.FC = () => {
  const [categories, setCategories] = useState<TestCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<TestCategory | null>(null);
  const [detailMode, setDetailMode] = useState<"view" | "edit">("view");

  const openDetail = (cat: TestCategory, mode: "view" | "edit") => {
    setDetailMode(mode);
    setEditing(cat);
  };

  const [showAddModal, setShowAddModal] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [addError, setAddError] = useState("");
  const [adding, setAdding] = useState(false);

  const [confirmToggle, setConfirmToggle] = useState<TestCategory | null>(null);

  // background=true skips the loading spinner. The spinner branch unmounts
  // DetailView; remounting it re-seeds form/config from the stale `editing`
  // object, visually reverting a save the DB accepted (and a second save
  // would then write the stale config back). Post-save refreshes must be
  // background so the open editor is never torn down.
  const loadCategories = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    setError(null);
    try {
      const data = await TestCategoryService.listCategories();
      setCategories(data || []);
      setEditing((prev) =>
        prev ? ((data || []).find((c) => c.id === prev.id) ?? prev) : prev,
      );
    } catch {
      setError("Failed to load test categories");
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleAdd = async () => {
    const name = newCatName.trim();
    if (!name) {
      setAddError("Category name is required");
      return;
    }
    setAdding(true);
    setAddError("");
    try {
      await TestCategoryService.createCategory({ name });
      setShowAddModal(false);
      setNewCatName("");
      await loadCategories();
    } catch (err: any) {
      setAddError(err?.response?.data?.message || "Failed to create category");
    } finally {
      setAdding(false);
    }
  };

  const handleClone = async (cat: TestCategory) => {
    try {
      await TestCategoryService.cloneCategory(cat.id);
      await loadCategories();
    } catch {
      setError("Failed to clone category");
    }
  };

  const doToggle = async () => {
    if (!confirmToggle) return;
    const cat = confirmToggle;
    setConfirmToggle(null);
    try {
      await TestCategoryService.toggleCategory(cat.id);
      await loadCategories();
    } catch {
      setError("Failed to toggle category status");
    }
  };

  return (
    <div className="tcs">
      <div className="tcs-header">
        <h1>Test Category Settings</h1>
        <p>
          Configure behaviour, ranking, proctoring and test instructions for
          each test category.
        </p>
      </div>

      {error && !editing && (
        <div className="tcs-alert tcs-alert-danger">
          <AlertCircle size={14} style={{ marginRight: 6 }} />
          {error}
        </div>
      )}

      {loading ? (
        <div className="tcs-loading">
          <Spinner role="status" animation="border" size="sm" /> Loading
          categories...
        </div>
      ) : editing ? (
        <DetailView
          key={editing.id}
          category={editing}
          initialMode={detailMode}
          onBack={() => setEditing(null)}
          onSaved={() => loadCategories(true)}
        />
      ) : (
        <ListView
          categories={categories}
          onOpen={openDetail}
          onClone={handleClone}
          onToggle={setConfirmToggle}
          onAdd={() => {
            setNewCatName("");
            setAddError("");
            setShowAddModal(true);
          }}
        />
      )}

      {/* Add Category Modal */}
      <Modal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            Add Test Category
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addError && (
            <div className="tcs-alert tcs-alert-danger">{addError}</div>
          )}
          <label className="tcs-label">Category Name</label>
          <input
            className="tcs-input"
            value={newCatName}
            onChange={(e) => setNewCatName(e.target.value)}
            placeholder="e.g. Unit Test, Mid-Term"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdd();
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <button
            className="tcs-btn tcs-btn-secondary"
            onClick={() => setShowAddModal(false)}
          >
            Cancel
          </button>
          <button
            className="tcs-btn tcs-btn-primary"
            onClick={handleAdd}
            disabled={adding}
          >
            {adding ? "Creating..." : "Create"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Enable/Disable confirmation */}
      <Modal
        show={!!confirmToggle}
        onHide={() => setConfirmToggle(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem" }}>
            {confirmToggle?.enableStatus === 0 ? "Enable" : "Disable"} Category
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to{" "}
          {confirmToggle?.enableStatus === 0 ? "enable" : "disable"}{" "}
          <strong>{confirmToggle?.name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <button
            className="tcs-btn tcs-btn-secondary"
            onClick={() => setConfirmToggle(null)}
          >
            Cancel
          </button>
          <button
            className={`tcs-btn ${
              confirmToggle?.enableStatus === 0
                ? "tcs-btn-success"
                : "tcs-btn-danger"
            }`}
            onClick={doToggle}
          >
            {confirmToggle?.enableStatus === 0 ? "Enable" : "Disable"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TestCategorySettingsPage;
